import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { WrapText } from "@mui/icons-material";
import {
  connectCharacterFields,
  disconnectCharacterFields,
  isConnectedCharacterField,
} from "../../../../../../utils/fieldConnections";
import { buildVisibleTableRows } from "../utils/tableFieldLayout";
import DraggablePaper from "./DraggablePaper";

const keyOf = (field) => field?.field_instance_id || field?.name;

const labelOf = (field) =>
  field?.field_label ||
  field?.label ||
  field?.field_name ||
  field?.name ||
  "Text field";

const isBoundary = (field) => String(field?.name || "").startsWith("block__");

const isTextWidget = (field) =>
  Boolean(
    field?.type === "PDFTextField" &&
      field.field_id &&
      keyOf(field) &&
      !isBoundary(field) &&
      !field.generic_table &&
      field.location !== "sign",
  );

const resolveTargets = (fields, requested) => {
  const byInstance = new Map(
    fields
      .filter((field) => field.field_instance_id)
      .map((field) => [field.field_instance_id, field]),
  );
  const byName = new Map(
    fields.filter((field) => field.name).map((field) => [field.name, field]),
  );

  return requested.map(
    (field) =>
      byInstance.get(field?.field_instance_id) || byName.get(field?.name),
  );
};

const getModeError = (fields, targets, mode) => {
  if (
    !targets.length ||
    !targets.every(isTextWidget) ||
    new Set(targets.map(keyOf)).size !== targets.length
  ) {
    return "Select tagged PDF text fields outside tables.";
  }

  const targetIds = new Set(targets.map((field) => field.field_id));

  if (mode === "character") {
    if (
      targets.some(
        (field) =>
          field.text_connection ||
          (field.connection && !isConnectedCharacterField(field)),
      )
    ) {
      return "Disconnect the existing connection first.";
    }

    const sourceConflict = fields.some(
      (field) =>
        targetIds.has(field.connection?.source_field_id) ||
        targetIds.has(field.text_connection?.source_field_id),
    );

    if (sourceConflict) {
      return "A connection source cannot also be a character box.";
    }
  } else {
    if (targets.length < 2 || targetIds.size !== 1) {
      return "Multiline requires at least two widgets tagged with the same field.";
    }

    const characterConflict =
      targets.some((field) => field.connection) ||
      fields.some(
        (field) =>
          field.connection?.mode === "character" &&
          (targetIds.has(field.field_id) ||
            targetIds.has(field.connection.source_field_id)),
      );

    if (characterConflict) {
      return "Disconnect the character connection first.";
    }
  }

  const metadataKey = mode === "character" ? "connection" : "text_connection";

  const connected = targets.filter((field) => field[metadataKey]);

  if (!connected.length) return "";

  const groupId = connected[0][metadataKey].group_id;

  if (
    !groupId ||
    connected.length !== targets.length ||
    connected.some((field) => field[metadataKey].group_id !== groupId)
  ) {
    return "Select one complete connected group, or disconnect it first.";
  }

  const selectedKeys = new Set(targets.map(keyOf));
  const entireGroup = fields.filter(
    (field) => field[metadataKey]?.group_id === groupId,
  );

  if (
    entireGroup.length !== targets.length ||
    entireGroup.some((field) => !selectedKeys.has(keyOf(field)))
  ) {
    return "Select every widget in the existing group.";
  }

  return "";
};

const buildSourceOptions = (catalog, fields, targets) => {
  const excludedIds = new Set(targets.map((field) => field?.field_id));

  fields.forEach((field) => {
    if (field.connection || field.text_connection) {
      excludedIds.add(field.field_id);
    }

    if (field.text_connection?.source_field_id) {
      excludedIds.add(field.text_connection.source_field_id);
    }
  });

  const byId = new Map();

  [...catalog, ...fields].forEach((field) => {
    if (field?.field_id && !byId.has(field.field_id)) {
      byId.set(field.field_id, field);
    }
  });

  // Keep saved sources available even if absent from the current catalog.
  fields.filter(isConnectedCharacterField).forEach((field) => {
    const connection = field.connection;

    if (!byId.has(connection.source_field_id)) {
      byId.set(connection.source_field_id, {
        field_id: connection.source_field_id,
        field_label: connection.source_field_label,
        field_type: "text",
      });
    }
  });

  return [...byId.values()]
    .filter((field) => {
      const type = String(
        field.field_type ||
          (field.type === "PDFTextField" ? "text" : field.type) ||
          "",
      ).toLowerCase();

      return (
        !excludedIds.has(field.field_id) &&
        !field.connection &&
        !field.text_connection &&
        !field.generic_table &&
        field.location !== "sign" &&
        ["text", "number", "multiline"].includes(type)
      );
    })
    .sort((a, b) => labelOf(a).localeCompare(labelOf(b)));
};

export default function FieldConnector({
  currentProcessingFile,
  setCurrentProcessingFile,
  catalog = [],
  pushHistory,
  disabled = false,
  onNotify,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("character");
  const [orderedTargets, setOrderedTargets] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [staticPrefix, setStaticPrefix] = useState("");
  const [error, setError] = useState("");

  const temp = currentProcessingFile?.document?.tempFields;
  const fields = temp?.formFields || [];
  const extras = temp?.selectedExtraFields || [];

  const requested = (extras.length ? extras : [temp?.selectedFormField]).filter(
    (field) => field && !isBoundary(field),
  );

  const resolvedSelection = resolveTargets(fields, requested);
  const hasUnresolved = resolvedSelection.some((field) => !field);
  const selected = [
    ...new Map(
      resolvedSelection.filter(Boolean).map((field) => [keyOf(field), field]),
    ).values(),
  ];

  // Keep the captured selection while the dialog is open.
  const targets = open ? resolveTargets(fields, orderedTargets) : selected;

  const characterError = getModeError(fields, targets, "character");
  const multilineError = getModeError(fields, targets, "multiline");

  const showControl =
    !hasUnresolved && selected.length > 1 && selected.every(isTextWidget);

  const options = buildSourceOptions(
    Array.isArray(catalog) ? catalog : [],
    fields,
    targets,
  );
  const source = options.find((field) => field.field_id === sourceId) || null;

  const modeError = mode === "character" ? characterError : multilineError;

  const editingConnection = targets.some(
    (field) => field?.connection || field?.text_connection,
  );

  const close = () => {
    setOpen(false);
    setError("");
  };

  const start = () => {
    if (disabled || !showControl) return;

    const existingCharacter = selected.find(isConnectedCharacterField);
    const existingText = selected.find((field) => field.text_connection);

    const initialMode = existingCharacter
      ? "character"
      : existingText || !multilineError
        ? "multiline"
        : "character";

    const selectionError = getModeError(fields, selected, initialMode);

    if (selectionError) {
      onNotify?.("error", selectionError);
      return;
    }

    let ordered = [...selected];

    if (existingCharacter) {
      ordered.sort(
        (a, b) => a.connection.character_index - b.connection.character_index,
      );
    } else if (existingText) {
      ordered.sort(
        (a, b) =>
          a.text_connection.widget_index - b.text_connection.widget_index,
      );
    } else {
      const layoutOrder = buildVisibleTableRows(
        selected,
        temp?.currentDocument || {},
      ).flatMap((row) => row.fields || []);

      const selectedByKey = new Map(
        selected.map((field) => [keyOf(field), field]),
      );

      if (
        layoutOrder.length === selected.length &&
        new Set(layoutOrder.map(keyOf)).size === selected.length &&
        layoutOrder.every((field) => selectedByKey.has(keyOf(field)))
      ) {
        ordered = layoutOrder.map((field) => selectedByKey.get(keyOf(field)));
      }
    }

    setOrderedTargets(ordered);
    setMode(initialMode);
    setSourceId(existingCharacter?.connection.source_field_id || "");
    setStaticPrefix(existingCharacter?.connection.static_prefix || "");
    setError("");
    setOpen(true);
  };

  const commit = (updatedFields) => {
    // Apply changed widgets while preserving unrelated current editor state.
    const changes = new Map(
      updatedFields
        .filter((field, index) => field !== fields[index])
        .map((field) => [keyOf(field), field]),
    );

    pushHistory?.();

    setCurrentProcessingFile((previous) => {
      const previousTemp = previous?.document?.tempFields;
      if (!previousTemp) return previous;

      const nextFields = (previousTemp.formFields || []).map(
        (field) => changes.get(keyOf(field)) || field,
      );
      const byKey = new Map(nextFields.map((field) => [keyOf(field), field]));
      const refresh = (field) =>
        field ? byKey.get(keyOf(field)) || field : field;

      return {
        ...previous,
        document: {
          ...previous.document,
          tempFields: {
            ...previousTemp,
            formFields: nextFields,
            selectedFormField: refresh(previousTemp.selectedFormField),
            selectedExtraFields: (previousTemp.selectedExtraFields || []).map(
              refresh,
            ),
          },
        },
      };
    });
  };

  const save = () => {
    if (disabled) return;

    try {
      const validationError = getModeError(fields, targets, mode);
      if (validationError) throw new Error(validationError);

      let updated;

      if (mode === "character") {
        if (!source) throw new Error("Select a source field.");

        const previousConnection = targets[0].connection;

        updated = connectCharacterFields(fields, targets, source, {
          groupId:
            previousConnection?.source_field_id === source.field_id
              ? previousConnection.group_id
              : crypto.randomUUID(),
          stripSeparators: Boolean(previousConnection?.strip_separators),
          staticPrefix,
        });
      } else {
        const previousConnection = targets[0].text_connection;
        const groupId = previousConnection?.group_id || crypto.randomUUID();
        const indexByKey = new Map(
          targets.map((field, index) => [keyOf(field), index]),
        );

        updated = fields.map((field) => {
          if (!indexByKey.has(keyOf(field))) return field;
          const width = Number(
            field?.rect?.W ?? field?.rect?.w ?? field?.width,
          );
          const height = Number(
            field?.rect?.H ?? field?.rect?.h ?? field?.height,
          );

          const isGeneric =
            Boolean(field?.is_generic) ||
            String(field?.field_id ?? "")
              .trim()
              .toLowerCase()
              .startsWith("generic_");

          const contentHeight = height - 1;
          const savedFontSize = Number(field?.fontSize);
          const requestedFontSize =
            Number.isFinite(savedFontSize) && savedFontSize > 0
              ? savedFontSize
              : 10;

          const shouldSetConnectedFont =
            isGeneric && Number.isFinite(contentHeight) && contentHeight >= 10;

          const connectedFontSize = Math.min(requestedFontSize, contentHeight);

          return {
            ...field,
            ...(shouldSetConnectedFont ? { fontSize: connectedFontSize } : {}),
            text_connection: {
              group_id: groupId,
              source_field_id: targets[0].field_id,
              source_field_label: labelOf(targets[0]),
              widget_index: indexByKey.get(keyOf(field)),
              widget_count: targets.length,
              width,
              height,
              ...(previousConnection?.source_value !== undefined
                ? { source_value: previousConnection.source_value }
                : {}),
            },
          };
        });
      }

      commit(updated);
      close();
      onNotify?.("success", "Fields connected");
    } catch (err) {
      setError(err.message);
    }
  };

  const disconnect = () => {
    if (disabled) return;

    try {
      const validationError = getModeError(fields, targets, mode);
      if (validationError) throw new Error(validationError);

      let updated;

      if (mode === "character") {
        updated = disconnectCharacterFields(fields, targets);
      } else {
        const selectedKeys = new Set(targets.map(keyOf));

        updated = fields.map((field) => {
          if (!selectedKeys.has(keyOf(field))) return field;

          const { text_connection, ...rest } = field;
          return rest;
        });
      }

      commit(updated);
      close();
      onNotify?.("success", "Connection removed");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      {showControl && (
        <Tooltip title="Connect fields">
          <span>
            <IconButton
              size="small"
              aria-label="Connect fields"
              disabled={disabled}
              onClick={start}
              sx={{
                border: "1px solid #d0d7de",
                borderRadius: 1.5,
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.08)",
                },
              }}
            >
              <WrapText fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Dialog
        PaperComponent={DraggablePaper}
        aria-labelledby="draggable-dialog-title"
        open={open}
        onClose={close}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
          },
        }}
      >
        <DialogTitle
          id="draggable-dialog-title"
          style={{ cursor: "move" }}
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          Connect fields
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack spacing={0.75}>
              <Typography variant="subtitle2">Connection type</Typography>

              <Box
                role="group"
                aria-label="Connection type"
                sx={{
                  display: "flex",
                  width: "fit-content",
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 999,
                  backgroundColor: "#eceff3",
                }}
              >
                {[
                  {
                    value: "multiline",
                    label: "Multiline",
                    reason: multilineError,
                  },
                  {
                    value: "character",
                    label: "Character",
                    reason: characterError,
                  },
                ].map((option) => (
                  <Tooltip key={option.value} title={option.reason || ""}>
                    <span>
                      <ButtonBase
                        disabled={disabled || Boolean(option.reason)}
                        aria-pressed={mode === option.value}
                        onClick={() => {
                          setMode(option.value);
                          setError("");
                        }}
                        sx={{
                          px: 2,
                          py: 0.8,
                          borderRadius: 999,
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          backgroundColor:
                            mode === option.value ? "#fff" : "transparent",
                          boxShadow:
                            mode === option.value
                              ? "0 1px 3px rgba(15,23,42,0.18)"
                              : "none",
                          "&.Mui-disabled": {
                            color: "text.disabled",
                          },
                        }}
                      >
                        {option.label}
                      </ButtonBase>
                    </span>
                  </Tooltip>
                ))}
              </Box>
            </Stack>

            {mode === "character" ? (
              <>
                <Autocomplete
                  options={options}
                  value={source}
                  disabled={disabled}
                  getOptionLabel={labelOf}
                  isOptionEqualToValue={(option, value) =>
                    option.field_id === value.field_id
                  }
                  onChange={(_, value) => {
                    setSourceId(value?.field_id || "");
                    setError("");
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;

                    return (
                      <li key={option.field_id} {...optionProps}>
                        {labelOf(option)}
                      </li>
                    );
                  }}
                  noOptionsText="No eligible source fields"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Connect to"
                      placeholder="Search fields"
                    />
                  )}
                />

                <Typography variant="body2" color="text.secondary">
                  Show one input and fill these {targets.length} boxes with one
                  character each, in order.
                </Typography>

                <TextField
                  label="Static prefix already printed on PDF"
                  placeholder="Example: B"
                  value={staticPrefix}
                  disabled={disabled}
                  onChange={(event) => {
                    setStaticPrefix(event.target.value);
                    setError("");
                  }}
                  helperText="Optional. This prefix stays in the input but is not placed into a connected box."
                />
              </>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={600}>
                  {labelOf(targets[0])}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Show one multiline input. Text continues into the next
                  selected field only when needed.
                </Typography>
              </>
            )}

            {editingConnection && (
              <Typography variant="caption" color="text.secondary">
                To change the connection type, disconnect this group first.
              </Typography>
            )}

            {(error || modeError) && (
              <Alert severity="error">{error || modeError}</Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          {editingConnection && (
            <Button
              color="error"
              disabled={disabled}
              onClick={disconnect}
              sx={{ mr: "auto" }}
            >
              Disconnect
            </Button>
          )}

          <Button onClick={close}>Cancel</Button>

          <Button
            variant="contained"
            disabled={
              disabled ||
              Boolean(modeError) ||
              (mode === "character" && !source)
            }
            onClick={save}
          >
            {editingConnection ? "Save connection" : "Connect"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
